<template>
  <!-- FT-191: вкладка «Безопасность» настроек компании — блокировка общих компьютеров по PIN.
       Видят superadmin и admin; включает функцию и меняет таймаут только superadmin (REQ-01).
       Устройства: superadmin — все устройства MainCompany, admin — своего филиала (REQ-02). -->
  <div data-test="security-settings">
    <v-card v-if="canEditPolicy" flat class="mb-4">
      <v-card-title>{{ $t("security_settings.pin_lock_title") }}</v-card-title>
      <v-card-text>
        <p class="text--secondary">{{ $t("security_settings.pin_lock_description") }}</p>
        <v-switch v-model="enabled" data-test="pin-lock-enabled" :label="$t('security_settings.pin_lock_enabled')" />
        <v-text-field
          v-model.number="idleMinutes"
          data-test="pin-lock-idle"
          type="number"
          min="1"
          max="120"
          :label="$t('security_settings.idle_minutes')"
          style="max-width: 280px"
        />
        <v-btn color="primary" :loading="savingPolicy" data-test="pin-lock-save" @click="savePolicy">
          {{ $t("security_settings.save") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card flat>
      <v-card-title>{{ $t("security_settings.devices_title") }}</v-card-title>
      <v-card-text>
        <div v-if="canRegister" class="d-flex align-center flex-wrap mb-4">
          <v-text-field
            v-model="newDeviceName"
            data-test="device-name"
            :label="$t('security_settings.device_name')"
            :disabled="!policyEnabled"
            class="mr-2"
            style="max-width: 280px"
          />
          <v-btn color="primary" :disabled="!policyEnabled || !newDeviceName" :loading="registering" data-test="device-register" @click="registerThisComputer">
            {{ $t("security_settings.make_shared") }}
          </v-btn>
          <div v-if="!policyEnabled" class="text--secondary ml-2">{{ $t("security_settings.enable_first") }}</div>
        </div>

        <div v-if="!devices.length" class="text--secondary">{{ $t("security_settings.no_devices") }}</div>
        <v-card v-for="device in devices" :key="device.id" outlined class="mb-3" data-test="device-card">
          <v-card-title class="text-subtitle-1">
            {{ device.name }}
            <v-chip v-if="currentDeviceId === device.id" small class="ml-2" color="primary">{{ $t("security_settings.this_computer") }}</v-chip>
            <v-chip v-if="device.revoked_at" small class="ml-2">{{ $t("security_settings.revoked") }}</v-chip>
          </v-card-title>
          <v-card-subtitle>
            {{ device.company_name }} · {{ $t("security_settings.registered_by") }}: {{ device.created_by ? device.created_by.name : "—" }}
            · {{ $t("security_settings.last_activity") }}: {{ formatTime(device.last_activity_at) }}
          </v-card-subtitle>
          <v-card-text>
            <div v-for="user in device.users" :key="user.id" class="d-flex align-center flex-wrap">
              <span class="mr-2">{{ user.name }}</span>
              <v-chip v-if="user.pin_disabled" x-small class="mr-2">{{ $t("security_settings.pin_disabled") }}</v-chip>
              <v-btn x-small text :disabled="!!device.revoked_at" @click="removeUser(device, user)">{{ $t("security_settings.remove_user") }}</v-btn>
              <v-btn x-small text @click="resetPin(user)">{{ $t("security_settings.reset_pin") }}</v-btn>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn small text @click="openEvents(device)">{{ $t("security_settings.journal") }}</v-btn>
            <v-btn v-if="!device.revoked_at" small text color="error" @click="revoke(device)">{{ $t("security_settings.revoke") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-card-text>
    </v-card>

    <v-dialog v-model="eventsOpen" max-width="640">
      <v-card>
        <v-card-title>{{ $t("security_settings.journal") }}<template v-if="eventsDevice">: {{ eventsDevice.name }}</template></v-card-title>
        <v-card-text>
          <div v-for="(event, i) in events" :key="i" class="py-1">
            <span class="text--secondary mr-2">{{ formatTime(event.created_at) }}</span>{{ eventLabel(event) }}
            <span v-if="event.ip" class="text--secondary ml-2">{{ event.ip }}</span>
          </div>
          <v-btn v-if="eventsNextPage" small text class="mt-2" @click="loadMoreEvents">{{ $t("security_settings.more") }}</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Capacitor } from "@capacitor/core";
import { writeDeviceSecret, clearDeviceSecret, writeIdleMinutes } from "@/utils/deviceLock";

export default {
  name: "SecuritySettingsTab",
  props: {
    // Подменяются в тестах; по умолчанию — настоящие реализации.
    isNative: { type: Boolean, default: () => Capacitor.isNativePlatform() },
    writeSecret: { type: Function, default: (secret) => writeDeviceSecret(secret) },
    clearSecret: { type: Function, default: () => clearDeviceSecret() },
  },
  data() {
    const mc = this.$store.getters.getMainCompany || {};
    return {
      enabled: !!mc.pin_lock_enabled,
      idleMinutes: mc.pin_lock_idle_minutes || 5,
      savingPolicy: false,
      devices: [],
      newDeviceName: "",
      registering: false,
      eventsOpen: false,
      eventsDevice: null,
      events: [],
      eventsNextPage: null,
    };
  },
  computed: {
    api() {
      return `${this.$store.getters.getApiUrl}/api/v1`;
    },
    canEditPolicy() {
      return !!this.$store.getters.isSuperAdmin;
    },
    policyEnabled() {
      const mc = this.$store.getters.getMainCompany || {};
      return !!mc.pin_lock_enabled;
    },
    canRegister() {
      return !this.isNative;
    },
    currentDeviceId() {
      const device = this.$store.getters["deviceLock/device"];
      return device ? device.id : null;
    },
  },
  created() {
    this.loadDevices();
  },
  methods: {
    async loadDevices() {
      try {
        const response = await this.axios.get(`${this.api}/trusted_devices`);
        this.devices = response.data || [];
      } catch (error) {
        this.devices = [];
      }
    },
    async savePolicy() {
      const minutes = Number(this.idleMinutes);
      if (!Number.isInteger(minutes) || minutes < 1 || minutes > 120) {
        this.$notify(this.$t("errors.attention"), this.$t("security_settings.idle_invalid"), "warning", 5000);
        return;
      }
      const mc = this.$store.getters.getMainCompany || {};
      this.savingPolicy = true;
      try {
        await this.axios.patch(`${this.api}/main_companies/${mc.id}`, {
          main_company: { pin_lock_enabled: this.enabled, pin_lock_idle_minutes: minutes },
        });
        this.$store.commit("setMainCompany", { ...mc, pin_lock_enabled: this.enabled, pin_lock_idle_minutes: minutes });
        // Новый таймаут — сразу всем открытым вкладкам этого компьютера (общее хранилище).
        writeIdleMinutes(minutes);
      } catch (error) {
        this.$notify(this.$t("errors.attention"), this.$t("security_settings.save_failed"), "error", 5000);
      } finally {
        this.savingPolicy = false;
      }
    },
    // OQ-03: компьютер становится общим, текущая сессия завершается — следующий вход уже в
    // режиме устройства (OTP или passkey).
    async registerThisComputer() {
      if (!this.newDeviceName) return;
      this.registering = true;
      try {
        const response = await this.axios.post(`${this.api}/trusted_devices`, { name: this.newDeviceName });
        this.writeSecret(response.data.secret);
        // refreshDevice уже завершает обычную сессию на ставшем общим компьютере (applyDevice);
        // повторный выход — только если она почему-то жива, иначе лишний DELETE sign_out → 401.
        await this.$store.dispatch("deviceLock/refreshDevice");
        if (this.$store.getters.isLoggedIn) await this.$store.dispatch("deviceLock/signOutQuietly");
      } catch (error) {
        this.$notify(this.$t("errors.attention"), this.$t("security_settings.register_failed"), "error", 5000);
      } finally {
        this.registering = false;
      }
    },
    async revoke(device) {
      const ok = await this.$confirm({
        title: this.$t("security_settings.revoke_confirm", { name: device.name }),
        confirmButtonText: this.$t("custom.yes"),
        cancelButtonText: this.$t("custom.no"),
        maxWidth: 500,
      });
      if (!ok) return;
      await this.axios.delete(`${this.api}/trusted_devices/${device.id}`);
      if (device.id === this.currentDeviceId) this.clearSecret();
      await this.loadDevices();
    },
    async removeUser(device, user) {
      await this.axios.delete(`${this.api}/trusted_devices/${device.id}/users/${user.id}`);
      await this.loadDevices();
    },
    async resetPin(user) {
      await this.axios.delete(`${this.api}/users/${user.id}/pin`);
      this.$notify(this.$t("security_settings.pin_reset_done"), "", "success", 3000);
    },
    fetchEventsPage(deviceId, page) {
      return this.axios.get(`${this.api}/trusted_devices/${deviceId}/events`, { params: { page } });
    },
    async openEvents(device) {
      this.eventsDevice = device;
      this.events = [];
      this.eventsOpen = true;
      const response = await this.fetchEventsPage(device.id, 1);
      this.events = response.data.events;
      this.eventsNextPage = response.data.next_page;
    },
    async loadMoreEvents() {
      if (!this.eventsNextPage || !this.eventsDevice) return;
      const response = await this.fetchEventsPage(this.eventsDevice.id, this.eventsNextPage);
      this.events = this.events.concat(response.data.events);
      this.eventsNextPage = response.data.next_page;
    },
    eventLabel(event) {
      const label = this.$t(`security_settings.events.${event.event}`);
      if (event.event === "switch" && event.from_user && event.user) {
        return `${label}: ${event.from_user.name} → ${event.user.name}`;
      }
      return event.user ? `${label}: ${event.user.name}` : label;
    },
    formatTime(value) {
      return value ? new Date(value).toLocaleString() : "—";
    },
  },
};
</script>
